/**
 * Constante que define las unidades de medida disponibles para cada tipo de posología.
 *
 * @description
 * Contiene los diferentes tipos de unidades de medida utilizadas en prescripciones médicas,
 * organizadas por categoría: receta general, receta magistral y uso general.
 *
 * @property {string[]} receta - Unidades específicas para recetas generales (medicamentos comerciales)
 * @property {string[]} magistral - Unidades específicas para recetas magistrales (preparaciones farmacéuticas)
 * @property {string[]} general - Unidades básicas de uso general
 */
export const POSOLOGY = {
  receta : [
    'mg',
    'Kit',
    'Set',
    'Unidad',
    'Cintas',
    'Agujas',
    'Capsula',
    'Jeringas',
    'Lancetas',
    'Sensores',
    'Aplicación',
    'Reservorios',
  ],
  magistral: ['g', 'ml', 'mg', 'Gel', 'Gotas', 'Unidad', 'Capsula', 'Sobres', 'Aplicación'],
  general  : ['g', 'ml', 'mg', 'Unidad'],
};

/**
 * Constante que define las unidades de medida con sus formas singular y plural.
 *
 * @description
 * Mapea cada unidad de medida a sus formas gramaticales en singular y plural,
 * permitiendo la correcta presentación textual según la cantidad especificada.
 * Incluye unidades farmacéuticas, métricas y temporales.
 *
 * @property {Object} [key] - Cada clave representa una unidad de medida
 * @property {string} [key].singular - Forma singular de la unidad
 * @property {string} [key].plural - Forma plural de la unidad
 */
export const MEASUREMENT_UNITS = {
  cucharadita: {singular: 'Cucharadita', plural: 'Cucharaditas'},
  reservorios: {singular: 'Reservorio', plural: 'Reservorios'},
  jeringas   : {singular: 'Jeringa', plural: 'Jeringas'},
  comprimido : {singular: 'Comprimido', plural: 'Comprimidos'},
  permanente : {singular: 'Permanencia', plural: 'Permanencia'},
  cucharada  : {singular: 'Cucharada', plural: 'Cucharadas'},
  ampolla    : {singular: 'Ampolla', plural: 'Ampollas'},
  cápsula    : {singular: 'Cápsula', plural: 'Cápsulas'},
  capsula    : {singular: 'Capsula', plural: 'Capsulas'},
  lanceta    : {singular: 'Lanceta', plural: 'Lancetas'},
  sensores   : {singular: 'Sensor', plural: 'Sensores'},
  semanas    : {singular: 'Semana', plural: 'Semanas'},
  aguja      : {singular: 'Aguja', plural: 'Agujas'},
  sobre      : {singular: 'Sobre', plural: 'Sobres'},
  cinta      : {singular: 'Cinta', plural: 'Cintas'},
  horas      : {singular: 'Hora', plural: 'Horas'},
  gotas      : {singular: 'Gota', plural: 'Gotas'},
  vial       : {singular: 'Vial', plural: 'Viales'},
  días       : {singular: 'Día', plural: 'Días'},
  meses      : {singular: 'Mes', plural: 'Meses'},
  kit        : {singular: 'Kit', plural: 'Kits'},
  set        : {singular: 'Set', plural: 'Sets'},
  sos        : {singular: 'SOS', plural: 'SOS'},
  mg         : {singular: 'mg', plural: 'mg'},
  gr         : {singular: 'gr', plural: 'gr'},
  ml         : {singular: 'ml', plural: 'ml'},
  g          : {singular: 'g', plural: 'g'},
};

/**
 * Obtiene la forma gramatical correcta (singular o plural) de una unidad de medida según la cantidad.
 *
 * @description
 * Función que determina si debe usarse la forma singular o plural de una unidad de medida
 * basándose en la cantidad especificada. Si la unidad no existe en MEASUREMENT_UNITS,
 * retorna la unidad tal como fue proporcionada.
 *
 * @param {string} unit - La unidad de medida a formatear
 * @param {number | string} qty - La cantidad que determina singular (0 o 1) o plural (>1)
 * @returns {string | undefined} La forma gramatical correcta de la unidad, o undefined si no se proporciona unidad
 *
 * @example
 * getUnit('horas', 1); // Retorna 'Hora'
 * getUnit('horas', 5); // Retorna 'Horas'
 * getUnit('mg', 100); // Retorna 'mg'
 */
export const getUnit = (unit, qty: number | string) => {
  if (!unit) return;
  if (!MEASUREMENT_UNITS[unit.toLowerCase()]) return unit;
  if (qty === 1) return MEASUREMENT_UNITS[unit.toLowerCase()].singular;
  if (qty === 0) return MEASUREMENT_UNITS[unit.toLowerCase()].singular;
  return MEASUREMENT_UNITS[unit.toLowerCase()].plural;
};
